<template>
  <div>
    <EnvironmentUnavailable v-if="envUnavailable" class="mb-6" />

    <!-- Cartes de synthèse -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
      <div
        v-for="card in cards"
        :key="card.label"
        class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow"
      >
        <div class="flex items-center justify-between mb-3">
          <div :class="['w-10 h-10 rounded-xl flex items-center justify-center', card.iconBg]">
            <Icon :icon="card.icon" :class="['w-5 h-5', card.iconColor]" />
          </div>
        </div>
        <p v-if="loading" class="h-8 w-16 bg-gray-100 rounded animate-pulse"></p>
        <p v-else class="text-3xl font-bold text-gray-900">{{ card.value }}</p>
        <p class="text-sm text-gray-500 mt-1">{{ card.label }}</p>
      </div>
    </div>

    <!-- Graphique volume -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
      <h3 class="font-semibold text-gray-900 mb-1">Volume de livraisons</h3>
      <p class="text-sm text-gray-500 mb-6">30 derniers jours — environnement {{ modeStore.label }}</p>

      <div v-if="loading" class="h-64 bg-gray-50 rounded-lg animate-pulse"></div>
      <div v-else-if="!hasVolume" class="h-64 flex flex-col items-center justify-center text-center">
        <Icon icon="solar:chart-2-bold" class="w-10 h-10 text-gray-300 mb-3" />
        <p class="text-gray-500">Aucune livraison sur les 30 derniers jours.</p>
        <router-link to="/docs" class="text-sm text-[#8C0004] hover:underline mt-2">
          Créer votre première livraison depuis la documentation
        </router-link>
      </div>
      <div v-else class="h-64">
        <Line :data="chartData" :options="chartOptions" />
      </div>
    </div>

    <!-- Répartition par statut -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <h3 class="font-semibold text-gray-900 mb-6">Répartition par statut</h3>
      <div v-if="loading" class="h-12 bg-gray-50 rounded animate-pulse"></div>
      <div v-else-if="statusRows.length === 0" class="text-sm text-gray-500">
        Aucune donnée à afficher.
      </div>
      <div v-else class="space-y-3">
        <div v-for="row in statusRows" :key="row.status" class="flex items-center gap-4">
          <span class="w-32 text-sm text-gray-600 flex-shrink-0">{{ row.label }}</span>
          <div class="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              :class="['h-full rounded-full', row.color]"
              :style="{ width: `${row.percent}%` }"
            ></div>
          </div>
          <span class="w-12 text-sm font-semibold text-gray-900 text-right">{{ row.count }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Icon } from '@iconify/vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js'
import { apiFor, isEnvironmentUnauthorized } from '../services/api'
import EnvironmentUnavailable from '../components/EnvironmentUnavailable.vue'
import { useModeStore } from '../store/mode'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler)

const modeStore = useModeStore()

const loading = ref(true)
const envUnavailable = ref(false)
const stats = ref<any>(null)

const cards = computed(() => [
  {
    label: 'Livraisons totales',
    value: stats.value?.totals.deliveries ?? 0,
    icon: 'solar:box-bold',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-700',
  },
  {
    label: 'Livrées',
    value: stats.value?.totals.delivered ?? 0,
    icon: 'solar:check-circle-bold',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
  },
  {
    label: 'En cours',
    value: stats.value?.totals.inProgress ?? 0,
    icon: 'solar:delivery-bold',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    label: 'Annulées',
    value: stats.value?.totals.canceled ?? 0,
    icon: 'solar:close-circle-bold',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-500',
  },
  {
    label: 'Incidents',
    value: stats.value?.totals.incidents ?? 0,
    icon: 'solar:danger-triangle-bold',
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
  },
])

const hasVolume = computed(() =>
  (stats.value?.dailyVolume || []).some((d: any) => d.count > 0)
)

const chartData = computed(() => ({
  labels: (stats.value?.dailyVolume || []).map((d: any) =>
    new Date(d.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
  ),
  datasets: [
    {
      label: 'Livraisons',
      data: (stats.value?.dailyVolume || []).map((d: any) => d.count),
      borderColor: '#8C0004',
      backgroundColor: 'rgba(140, 0, 4, 0.08)',
      fill: true,
      tension: 0.35,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 2,
    },
  ],
}))

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: '#f3f4f6' } },
    x: { grid: { display: false }, ticks: { maxTicksLimit: 8 } },
  },
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  CREATED: { label: 'Créées', color: 'bg-gray-400' },
  ASSIGNED: { label: 'Assignées', color: 'bg-blue-500' },
  PICKED_UP: { label: 'En cours', color: 'bg-[#8C0004]' },
  DELIVERED: { label: 'Livrées', color: 'bg-emerald-500' },
  CANCELED: { label: 'Annulées', color: 'bg-gray-300' },
  INCIDENT: { label: 'Incidents', color: 'bg-orange-500' },
}

const statusRows = computed(() => {
  const byStatus = stats.value?.byStatus || {}
  const total = Object.values(byStatus).reduce((a: number, b: any) => a + b, 0) as number
  if (!total) return []
  return Object.entries(byStatus).map(([status, count]) => ({
    status,
    label: STATUS_META[status]?.label || status,
    color: STATUS_META[status]?.color || 'bg-gray-400',
    count: count as number,
    percent: Math.round(((count as number) / total) * 100),
  }))
})

onMounted(loadStats)

async function loadStats() {
  loading.value = true
  try {
    const { data } = await apiFor(modeStore.mode).get('/partner-portal/dashboard/stats')
    stats.value = data
  } catch (err) {
    envUnavailable.value = isEnvironmentUnauthorized(err)
    console.error('Erreur chargement statistiques:', err)
    stats.value = null
  } finally {
    loading.value = false
  }
}
</script>
